<template>
  <div class="d-flex SiteVersionComments__comments_container" id='SiteVersionComments__comments_container'>
    <div
      class="SiteVersionComments__comments_block"
      id="SiteVersionComments__comments_block"
      v-if="comments.length > 0 && !loading"
    >
      <div
        v-for="comment in comments"
        class="SiteVersionComments__comment"
        :id="'comment_' + comment.id"
      >
        <SiteVersionComment :comment="comment" :version="version" />
      </div>
    </div>
    <div class="SiteVersionComments__noComment" v-else-if="!loading">
      <the-icon class="SiteVersionComments__comment_icon" icon="comment" />
      <h3>No comments yet</h3>
      <div class="no_comments_small">
        Once you start writing and receiving comments they’ll appear here...
      </div>
    </div>
    <div v-else-if="loading" class="spinner mt-12" align="center">
      <FlamePreloader :compact="true" :size="100" />
    </div>

    <v-footer class="SiteVersionComments__footer" fixed>
      <SiteVersionMentionInput
        @input="send"
        :placeholder="placeholder"
        ref="childComponent"
        @submit="handleInputSubmit"
      />
      <v-btn class="y1 SiteVersionComments__footer_btn" @click="addComment">
        <v-icon> mdi-send </v-icon>
      </v-btn>
    </v-footer>
  </div>
</template>

<script>
import FlamePreloader from '@/components/_Common/FlamePreloader'
import SiteVersionMentionInput from '@/components/TheSite/SiteVersion/SiteVersionComments/SiteVersionMentionInput'
import SiteVersionComment from '@/components/TheSite/SiteVersion/SiteVersionComments/SiteVersionComment'
import AIContextProvider from '@/utils/aiContextProvider'
import promptApiService from '@/services/promptApiService'
import { PROMPT_IDS } from '@/constants/prompts'
import _ from 'lodash'
export default {
  name: 'SiteVersionComments',
  components: {
    SiteVersionMentionInput,
    FlamePreloader,
    SiteVersionComment,
  },
  props: {
    comments: Array,
    placeholder: String,
    version: Object,
    loading: Boolean,
    cloudCode: Number,
  },
  data() {
    return {
      msg: '',
      mentions: [],
      aiContextProvider: null,
    }
  },
  watch: {
    comments: {
      handler(newVal, oldVal) {
        if (newVal.length > 0)
          setTimeout(this.scrollToBottom, 500)
      }
    }
  },
  methods: {
    send(v) {
      this.msg = v.value
      this.mentions = v.mentions
    },
    handleInputSubmit() {
      // Get the latest value from the input before submitting
      const inputComponent = this.$refs.childComponent;
      if (inputComponent) {
        this.send({ value: inputComponent.value, mentions: inputComponent.d_mentions });
      }
      this.addComment();
    },
    scrollToBottom() {
      let container = document.getElementById('SiteVersionComments__comments_container')
      if (container) container.scrollTop = container.scrollHeight
    },
    async addComment() {
      // Get current user ID with proper null checking
      const currentUser = this.$store.state.user?.current
      const currentUserId = currentUser?.id
      
      if (!currentUserId) {
        console.error('No current user found')
        return
      }

      let siteUsers = _.cloneDeep(this.$store.state.site_user?.list || [])
      let usersIds = siteUsers
        .filter((su) => this.mentions.includes(su.id))
        .map((su) => su.user_id)

      // Create the initial comment
      await this.$store.dispatch('comments/create', {
        message: this.msg,
        user_id: currentUserId,
        mentioned_users: usersIds,
        version_id: this.version?.id,
      })

      // Check if the message contains a forge mention by looking for the data-id attribute
      const hasForgeAIMention = this.msg.includes('data-id="forge-ai"')

      if (hasForgeAIMention) {
        console.log('Forge AI mentioned - triggering response')
        // Extract the actual text without HTML tags for the AI prompt
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = this.msg
        const cleanMessage = tempDiv.textContent || tempDiv.innerText

        this.msg = cleanMessage // Set clean text for AI processing
        this.handleForgeAIResponse()
      }

      this.$refs.childComponent.clearInput()
    },

    async preparePromptArguments(userMessage) {
      // Initialize context provider if not already done
      if (!this.aiContextProvider) {
        this.aiContextProvider = new AIContextProvider(this.$store)
      }

      // Get comprehensive context based on user message
      const context = await this.aiContextProvider.getContext(userMessage, {
        comments: this.comments
      })

      // Format context for AI prompt
      const contextString = this.aiContextProvider.formatContextForPrompt(context)

      return {
        contextString: contextString,
        userMessage: userMessage,
        siteName: this.$store.state.site?.current?.name || 'Unknown',
        siteUrl: this.$store.state.site?.current?.url || 'Unknown',
        currentUserId: this.$store.state.user?.current?.id || null,
        versionId: this.version?.id || null,
        totalComments: this.comments?.length || 0
      }
    },

    async handleForgeAIResponse() {
      // Get current user ID with proper null checking
      const currentUser = this.$store.state.user?.current
      const currentUserId = currentUser?.id
      
      if (!currentUserId) {
        console.error('No current user found')
        return
      }

      let res = {}
      try {
        const userMessage = this.msg.replace(/@forge/g, '').trim()
        const promptArguments = await this.preparePromptArguments(userMessage)

        // Use prompt ID 1 for site version comments
        const response = await promptApiService.callPrompt(
          PROMPT_IDS.SITE_VERSION_COMMENTS,
          promptArguments,
          {
            responseJson: false
          }
        )

        const messageContent = response.choices[0]?.message?.content || 'No response received'

        res = {
          message: messageContent,
          user_id: currentUserId,
          mentioned_users: [],
          commented_by: 1,
          version_id: this.version?.id,
        }

      } catch (error) {
        console.error('Error getting AI response:', error)
        
        // Handle specific error types
        const errorInfo = promptApiService.handleError(error)
        console.error('Prompt API Error:', errorInfo)

        res = {
          message: "Sorry, I couldn't process that request. Please try again.",
          user_id: currentUserId,
          mentioned_users: [],
          commented_by: 1,
          version_id: this.version?.id,
        }

      }
      if (_.isEmpty(res)) return

      this.$store.dispatch('comments/create', res)

    },
  },
  mounted() {
    this.scrollToBottom
  },
}
</script>

<style lang="scss" scoped>
$style: SiteVersionComments;
.spinner {
  height: 53vh;
}
.#{$style} {
  &__comments_container {
    justify-content: center;
    overflow-y: scroll;
    flex-direction: column;
    height: calc(100vh - 244px);
    overscroll-behavior: contain;
  }

  &__comment {
    display: inherit;
  }

  &__noComment {
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
    height: calc(100vh - 231px);
    h3 {
      font-size: 14px;
      font-family: 'Instrument Sans', sans-serif;
      font-weight: 500;
      line-height: 20px;
    }
    .no_comments_small {
      margin-top: 8px;
      width: 344px;
      text-align: center;
    }
    .#{$style}__comment_icon {
      margin-bottom: 15px;
    }
    div {
      color: $N2;
    }
  }

  &__comments_block {
    padding-top: 16px;
    padding-left: 16px;
    padding-right: 16px;
    display: flex;
    flex-shrink: 0;
    flex-direction: column-reverse;
    width: 100%;
    flex-grow: 1;
  }

  &__footer {
    min-height: 80px;
    padding: 16px 28px 24px 28px;
    align-items: flex-start;
    border-radius: 0px 0px 0px 12px;
    border-top: 1px solid $N7;
    background: $N5;
    padding-right: 120px; // Add enough space for Intercom widget
    .#{$style}__footer_btn {
      position: absolute;
      right: 96px; // Move button left by Intercom width + some gap
    }
  }
}
</style>
